import { useState } from 'react'
import { Button, Card, Chip, EmptyState, Skeleton } from '../ui'
import { ApiError } from '../../api/client'
import { useInfiniteVendorDataRows, useVendorDataTables } from '../../api/hooks'
import { useAuthStore } from '../../stores/auth'
import type { AccessSystem, VendorRow } from '../../api/types'

function errDetail(e: unknown): string {
  if (e instanceof ApiError) return e.detail
  if (e instanceof Error) return e.message
  return 'Request failed'
}

/** One vendor row: label line + expandable pretty JSON of the raw fields
 *  (same pre style as the events modal's Raw payload block). */
function VendorRowItem({ row }: { row: VendorRow }) {
  const [open, setOpen] = useState(false)
  return (
    <li className="rounded-sm border border-ostrel-line bg-ostrel-card">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left hover:bg-ostrel-sand"
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-ostrel-ink">{row.label}</span>
          <span className="tnum block font-mono text-[11px] text-ostrel-ink-muted">{row.uid}</span>
        </span>
        <span className="ml-auto text-xs text-ostrel-ink-muted">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <pre className="tnum mx-3.5 mb-3 max-h-64 overflow-auto rounded-sm border border-ostrel-line bg-ostrel-sand p-3 font-mono text-[11px] leading-relaxed text-ostrel-ink">
          {JSON.stringify(row.fields, null, 2)}
        </pre>
      )}
    </li>
  )
}

/** Rows of the selected vendor table, cursor-paged with a Load more button. */
function VendorRows({ systemUid, table }: { systemUid: string; table: string }) {
  const rows = useInfiniteVendorDataRows(systemUid, table)

  if (rows.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-11" />
        <Skeleton className="h-11" />
      </div>
    )
  }
  if (rows.isError) {
    return <EmptyState title="Could not load rows" hint={errDetail(rows.error)} />
  }
  const items = rows.data.pages.flatMap((p) => p.items)
  if (items.length === 0) {
    return <EmptyState title="No rows" hint="This table holds no rows for the system." />
  }
  return (
    <div className="flex flex-col gap-2">
      <ul className="flex flex-col gap-2">
        {items.map((row) => (
          <VendorRowItem key={row.uid} row={row} />
        ))}
      </ul>
      {rows.hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          disabled={rows.isFetchingNextPage}
          onClick={() => rows.fetchNextPage()}
        >
          {rows.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </div>
  )
}

/**
 * The vendor raw-data browser for one System: one chip per vendor-mirror
 * table the connector declares (with a live row count) — click a chip to page
 * through its rows, click a row to expand the full field dict as JSON.
 * Read-only: the backend exposes the mirror rows exactly as the vendor said.
 */
export function VendorDataCard({ system }: { system: AccessSystem }) {
  // Admin-only surface (the API 403s viewers); don't mount the card at all.
  const isAdmin = useAuthStore((s) => s.customer?.role === 'admin')
  const tables = useVendorDataTables(system.unique_id, isAdmin)
  const [selected, setSelected] = useState<string | null>(null)

  if (!isAdmin) return null

  return (
    <Card title="Raw data">
      {tables.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
        </div>
      ) : tables.isError ? (
        <EmptyState title="Could not load vendor data" hint={errDetail(tables.error)} />
      ) : (tables.data?.length ?? 0) === 0 ? (
        <EmptyState
          title="No vendor data"
          hint="This provider mirrors no vendor tables for the browser."
        />
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {tables.data?.map((t) => (
              <button
                key={t.table}
                type="button"
                aria-pressed={selected === t.table}
                onClick={() => setSelected((cur) => (cur === t.table ? null : t.table))}
                className={`flex items-center gap-2 rounded-sm border px-3 py-2 text-sm transition-colors ${
                  selected === t.table
                    ? 'border-ostrel-gold bg-ostrel-sand'
                    : 'border-ostrel-line bg-ostrel-card hover:bg-ostrel-sand'
                }`}
              >
                <span className="font-medium text-ostrel-ink">{t.label}</span>
                <Chip tone="muted" label={String(t.count)} />
              </button>
            ))}
          </div>
          {selected && <VendorRows systemUid={system.unique_id} table={selected} />}
        </div>
      )}
    </Card>
  )
}
